import React from 'react';
import ReviewIcon from 'assets/images/riviewdicon.svg';
import { prepareLottoSalesDetails } from '../../../../utils/tableUtils';
import { formatCurrency } from 'utils';
import type { AuditSummaryContext, ManualLottoTileConfig, TileConfig } from '../types';
import { renderTitleWithInfoIcon, resolveVarianceBadge } from './shared';

export function buildLottoSalesTile(ctx: AuditSummaryContext): TileConfig {
  if (!ctx.isAllShift) {
    return {
      id: 'lotto_sales',
      visible: false,
      variant: 'standard_summary',
      title: 'LOTTO SALES',
    };
  }

  if (ctx.manualDayEntryEditability.lotto) {
    const variance = ctx.dailyTotals?.reconciliation_variance?.lotto_sales_variance ?? 0;
    const posTotal = ctx.dailyTotals?.pos?.lotto_sales ?? 0;

    const config: ManualLottoTileConfig = {
      id: 'lotto_sales',
      visible: true,
      variant: 'manual_lotto_sales',
      title: 'LOTTO SALES',
      variance,
      posTotal,
      cardClassName: ctx.mode === 'bothNotConnected' ? 'compact-tall-manual-card' : 'manual-entry-card',
      footer: ctx.mode === 'posOnly' ? `POS Total: ${formatCurrency(posTotal)}` : 'Manual Entry',
    };

    return config;
  }

  const lottoSalesInLotto = ctx.dailyTotals?.lotto?.sales ?? 0;
  const lottoSalesInPOS = ctx.dailyTotals?.pos?.lotto_sales ?? 0;
  const lottoSalesVariance = ctx.dailyTotals?.reconciliation_variance?.lotto_sales_variance ?? 0;

  if (ctx.mode === 'bothConnected') {
    const hasLottoSalesInput = ctx.isPosValueMissing && ctx.posMissingLottoSalesInput !== '';
    const effectivePosLottoSales = hasLottoSalesInput
      ? Number(ctx.posMissingLottoSalesInput) || 0
      : lottoSalesInPOS;
    const effectiveLottoSalesVariance = effectivePosLottoSales - lottoSalesInLotto;

    const badge = resolveVarianceBadge(effectiveLottoSalesVariance);
    const hasNoVariance = effectiveLottoSalesVariance === 0;

    const posPayout = ctx.dailyTotals?.pos?.payout ?? 0;
    const isPosZero = effectivePosLottoSales === 0 && posPayout === 0;
    const lottoPayoutsTotal = ctx.dailyTotals?.lotto?.payouts_total ?? 0;
    const freeTicketsPaid = ctx.dailyTotals?.lotto?.scratcher_free_tickets_paid_amount ?? 0;
    const isLottoZero = lottoSalesInLotto === 0 && lottoPayoutsTotal === 0 && freeTicketsPaid === 0;

    const details = prepareLottoSalesDetails(
      effectivePosLottoSales,
      lottoSalesInLotto,
      effectiveLottoSalesVariance,
      isPosZero,
      isLottoZero,
      true
    );

    return {
      id: 'lotto_sales',
      visible: true,
      variant: 'standard_lotto_pos',
      title: renderTitleWithInfoIcon('LOTTO SALES'),
      badge,
      value: hasNoVariance ? (
        <img
          src={ReviewIcon}
          alt="Review"
          className="review-icon"
          style={{ width: '32px', height: '32px' }}
        />
      ) : (
        formatCurrency(effectiveLottoSalesVariance)
      ),
      footer: `Total: ${formatCurrency(effectivePosLottoSales)}`,
      actionLabel: 'Verified',
      cardClassName: ctx.isPosValueMissing ? 'pos-missing-input-card' : undefined,
      inputPlaceholder: ctx.isPosValueMissing ? 'Enter POS Sales' : undefined,
      popover:
        typeof details === 'object' && details != null && 'calculations' in details
          ? { calculations: details.calculations, showZeroDiscrepancyPopover: true }
          : undefined,
    };
  }

  if (ctx.mode === 'posOnly') {
    return {
      id: 'lotto_sales',
      visible: true,
      variant: 'standard_summary',
      title: 'LOTTO SALES',
      value: formatCurrency(lottoSalesVariance),
      footer: `POS Total: ${formatCurrency(lottoSalesInPOS)}`,
      actionLabel: 'Verified in Lottery',
    };
  }

  if (ctx.mode === 'lottoOnly') {
    return {
      id: 'lotto_sales',
      visible: true,
      variant: 'standard_summary',
      title: 'LOTTO SALES',
      value: formatCurrency(lottoSalesInLotto),
      footer: 'Reported by Lottery',
      actionLabel: 'Verified in POS',
      cardClassName: 'compact-tall-summary-card',
    };
  }

  if (ctx.mode === 'bothNotConnected') {
    return {
      id: 'lotto_sales',
      visible: true,
      variant: 'standard_summary',
      title: 'LOTTO SALES',
      value: 'Match lotto sales reported by POS with sales in lottery portal.',
      footer: '',
      actionLabel: 'Verified in both',
      className: 'centered-content',
      valueClassName: 'hardcoded-value',
    };
  }

  return {
    id: 'lotto_sales',
    visible: true,
    variant: 'standard_summary',
    title: 'LOTTO SALES',
    value: formatCurrency(lottoSalesInLotto),
    footer: 'Reported by Lottery',
    actionLabel: 'Mark Verified',
  };
}

